import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import AdminLayout from "../../components/app/AdminLayout";
import { Card, Loader } from "./Dashboard";
import { Save, Eye, EyeOff } from "lucide-react";
import RichTextEditor from "../../components/app/RichTextEditor";

export default function Settings() {
  const { refresh: refreshSettings } = useSettings();
  const [settings, setSettings] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [saved, setSaved] = useState(false);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/settings").then(({ data }) => setSettings(data));
    adminApi.get("/admin/statuses").then(({ data }) => setStatuses(data.items));
  }, []);

  const save = async () => {
    setSaved(false);
    await adminApi.patch("/admin/settings", settings);
    await refreshSettings();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!settings) return <AdminLayout title="Settings"><Loader /></AdminLayout>;

  return (
    <AdminLayout title="Settings" subtitle="Manage your application settings"
      actions={<button onClick={save} data-testid="settings-save-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]"><Save size={14} /> Save</button>}>
      {saved && <div className="mb-4 p-3 border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] text-sm">Settings saved.</div>}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="General">
          <Field label="Organisation name"><Input value={settings.org_name || ""} onChange={(v) => setSettings({...settings, org_name: v})} testid="org-name" /></Field>
          <Field label="Organisation contact email"><Input value={settings.org_email || ""} onChange={(v) => setSettings({...settings, org_email: v})} placeholder="casting@yourorg.com" testid="org-email" />
            <p className="text-[10px] text-[#6B6862] mt-1">Shown to applicants in automated emails (the <span className="font-mono">{`{{org_email}}`}</span> variable) so they can reach you if they have issues.</p>
          </Field>
          <Field label="Site name (header branding)"><Input value={settings.site_name || ""} onChange={(v) => setSettings({...settings, site_name: v})} /></Field>
          <Field label="Public site URL (used in email links)"><Input value={settings.site_url || ""} onChange={(v) => setSettings({...settings, site_url: v})} placeholder="https://yoursite.com" /></Field>
          <Field label="Date Format">
            <select
              data-testid="date-format-select"
              value={settings.date_format || "uk"}
              onChange={(e) => setSettings({ ...settings, date_format: e.target.value })}
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood"
            >
              <option value="uk">UK — dd/mm/yyyy (e.g. {formatPreview("uk")})</option>
              <option value="us">US — mm/dd/yyyy (e.g. {formatPreview("us")})</option>
              <option value="iso">ISO — yyyy-mm-dd (e.g. {formatPreview("iso")})</option>
            </select>
            <p className="text-[10px] text-[#6B6862] mt-1">Applies to all dates displayed across the admin dashboard and actor portal.</p>
          </Field>
        </Card>

        <Card title="Email — Resend">
          <Field label="Resend API Key">
            <div className="relative">
              <input
                data-testid="resend-key"
                type={showKey ? "text" : "password"}
                value={settings.resend_api_key || ""}
                onChange={(e) => setSettings({...settings, resend_api_key: e.target.value})}
                placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 pr-10 text-sm font-mono focus:outline-none focus:border-blood"
              />
              <button type="button" onClick={() => setShowKey((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B6862] hover:text-[#F2EBE1]">
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="text-[10px] text-[#6B6862] mt-1">Get your key from <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blood hover:underline">resend.com</a> → API Keys</p>
          </Field>
          <Field label="Sender email">
            <Input value={settings.sender_email || ""} onChange={(v) => setSettings({...settings, sender_email: v})} placeholder="onboarding@resend.dev" testid="sender-email" />
            <p className="text-[10px] text-[#6B6862] mt-1">In test mode emails only go to verified addresses. Verify your domain in Resend for production sending.</p>
          </Field>
          <Field label="From name">
            <Input value={settings.from_name || ""} onChange={(v) => setSettings({...settings, from_name: v})} placeholder="Stourbridge Scare Maze" testid="from-name" />
            <p className="text-[10px] text-[#6B6862] mt-1">The sender display name shown in the recipient's inbox. If left blank, the organisation name is used.</p>
          </Field>
        </Card>

        <Card title="Contract Text" className="lg:col-span-2">
          <p className="text-xs text-[#6B6862] mb-3">This is the full contract actors read and digitally accept via the secure link in their role offer email. Use the toolbar for bold, italics, headings and lists — formatting is shown exactly as written on the acceptance page.</p>
          <RichTextEditor
            testid="contract-text-editor"
            value={settings.contract_text || ""}
            onChange={(html) => setSettings({ ...settings, contract_text: html })}
          />
          {!(settings.contract_text || "").replace(/<[^>]*>/g, "").trim() && (
            <p className="text-[11px] text-[#F59E0B] mt-2" data-testid="contract-empty-warning">
              ⚠ No contract text set. The role acceptance page will not let actors accept until you add and save your contract here.
            </p>
          )}
        </Card>

        <Card title="Application Statuses" className="lg:col-span-2">
          <div className="grid md:grid-cols-2 gap-2">
            {statuses.map((s) => (
              <div key={s.key} className="flex items-center gap-3 p-3 border border-[#1a1a1a]">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: s.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{s.label}</div>
                  <div className="text-[10px] text-[#6B6862]">{s.description}</div>
                </div>
                <span className="font-mono text-[10px] text-[#6B6862]">{s.key}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-[#6B6862]">Status colors and labels are pre-configured to match the dashboard theme. Custom statuses can be added in a future update.</p>
        </Card>
      </div>
    </AdminLayout>
  );
}

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="label-mini block mb-1.5">{label}</label>
    {children}
  </div>
);

const Input = ({ value, onChange, placeholder, testid, type = "text" }) => (
  <input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
    className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
);

const formatPreview = (fmt) => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  if (fmt === "us") return `${mm}/${dd}/${yyyy}`;
  if (fmt === "iso") return `${yyyy}-${mm}-${dd}`;
  return `${dd}/${mm}/${yyyy}`;
};
